import { Alert, Button, Field, Input } from '@marrafa-gioale/gymx-ui-web'
import { zodResolver } from '@hookform/resolvers/zod'
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { ShieldCheck } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { ApiError, ERROR_CODES, errorMessage } from '@/api/errors'
import {
  cancelMfaChallenge,
  getSnapshot,
  login,
  NotAnAdminError,
  verifyMfa,
} from '@/auth/session-store'
import { Logo } from '@/components/layout/logo'
import { forgotPassword, resetPassword } from '@/api/endpoints/auth'
import { config } from '@/config'

const searchSchema = z.object({
  /** Destinazione a cui tornare dopo il login. */
  redirect: z.string().optional(),
  /**
   * Il token del link «reimposta la password».
   *
   * Sta qui e non in una rotta propria per la stessa ragione per cui il secondo fattore
   * non ha una `/login/2fa`: è un passaggio della stessa pagina, e una rotta in più
   * sarebbe una guardia in più da scrivere e da sbagliare. Chi arriva con il token vede
   * il modulo della password nuova; chi arriva senza vede il login.
   */
  resetToken: z.string().optional(),
})

export const Route = createFileRoute('/login')({
  validateSearch: searchSchema,
  beforeLoad: ({ search }) => {
    // Già autenticato: la pagina di login non ha senso.
    if (getSnapshot().state.status === 'authenticated') {
      throw redirect({ to: search.redirect ?? '/' })
    }
  },
  component: LoginPage,
})

/**
 * La lunghezza minima è **8** caratteri (`Password` nel contract), non 6.
 *
 * La validazione qui evita un round-trip inutile su un errore ovvio; l'autorità resta
 * il server, che rifiuta anche le password in denylist con `422 password_too_common` —
 * cosa che il client non può sapere.
 */
const formSchema = z.object({
  email: z.string().trim().min(1, 'Inserisci la tua email.').email('Indirizzo email non valido.'),
  password: z.string().min(1, 'Inserisci la password.'),
})

type FormValues = z.infer<typeof formSchema>

/**
 * Secondo passaggio — OPS-04.
 *
 * Il codice TOTP è di sei cifre; quello di recupero ha forma `XXXXX-XXXXX` e il
 * server lo accetta con o senza trattino. Si validano solo le lunghezze, perché
 * l'autorità è il server: un codice formalmente giusto ma scaduto è comunque
 * `mfa_invalid_code`, e nessun controllo qui può saperlo.
 */
const codeSchema = z.object({
  code: z.string().trim().min(1, 'Inserisci il codice.'),
})

type CodeValues = z.infer<typeof codeSchema>

function LoginPage() {
  const navigate = useNavigate()
  const search = Route.useSearch()
  const [formError, setFormError] = useState<string | null>(null)
  const [rateLimitedFor, setRateLimitedFor] = useState<number | null>(null)
  /**
   * Il passaggio in cui siamo, in `useState` e non in una rotta a sé.
   *
   * Una `/login/2fa` servirebbe solo se il buono dovesse sopravvivere a una
   * navigazione, e non deve: vive in memoria e muore col ricaricamento, che è il
   * comportamento voluto. Una rotta in più significherebbe invece una guardia in
   * più da scrivere e da sbagliare.
   */
  const [step, setStep] = useState<'credentials' | 'code'>('credentials')
  /** Il recupero password: il modulo si apre sotto il login, non altrove. */
  const [recovering, setRecovering] = useState(false)
  /** Il codice di recupero è la via d'uscita quando il telefono non c'è più. */
  const [useRecoveryCode, setUseRecoveryCode] = useState(false)

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { email: '', password: '' },
  })

  const codeForm = useForm<CodeValues>({
    resolver: zodResolver(codeSchema),
    defaultValues: { code: '' },
  })

  /** Errori comuni ai due passaggi: rate limiting, ruolo insufficiente, resto. */
  const showError = (error: unknown) => {
    if (error instanceof ApiError && error.code === ERROR_CODES.rateLimited) {
      setRateLimitedFor(error.retryAfterSeconds ?? null)
    }
    setFormError(errorMessage(error))
  }

  const onSubmit = async (values: FormValues) => {
    setFormError(null)
    setRateLimitedFor(null)

    try {
      const result = await login(values.email, values.password)
      if (result.kind === 'mfa_required') {
        setStep('code')
        return
      }
      await navigate({ to: search.redirect ?? '/' })
    } catch (error) {
      if (error instanceof NotAnAdminError) {
        setFormError(error.message)
        return
      }

      if (error instanceof ApiError) {
        // `validation_failed` porta i dettagli per campo: si agganciano ai controlli
        // invece di mostrare un messaggio generico in cima al form.
        if (error.code === ERROR_CODES.validationFailed && error.fieldErrors.length > 0) {
          for (const field of error.fieldErrors) {
            if (field.field === 'email' || field.field === 'password') {
              form.setError(field.field, { message: field.message })
            }
          }
          if (error.fieldErrors.every((f) => f.field !== 'email' && f.field !== 'password')) {
            setFormError(error.message)
          }
          return
        }
      }

      showError(error)
    }
  }

  const onSubmitCode = async (values: CodeValues) => {
    setFormError(null)
    setRateLimitedFor(null)

    try {
      await verifyMfa(
        useRecoveryCode ? { recoveryCode: values.code } : { code: values.code.replace(/\s/g, '') },
      )
      await navigate({ to: search.redirect ?? '/' })
    } catch (error) {
      if (error instanceof NotAnAdminError) {
        // Il buono è già stato consumato: non si può ritentare il codice, si
        // riparte dalla password.
        backToCredentials()
        setFormError(error.message)
        return
      }
      if (error instanceof ApiError && error.code === ERROR_CODES.mfaInvalidCode) {
        codeForm.setError('code', { message: error.message })
        codeForm.setValue('code', '')
        return
      }
      showError(error)
    }
  }

  const backToCredentials = () => {
    cancelMfaChallenge()
    codeForm.reset({ code: '' })
    setUseRecoveryCode(false)
    setStep('credentials')
  }

  // Il token del link viene prima di tutto: chi ci arriva ha già in mano la prova, e
  // mostrargli il login significherebbe chiedergli la password che sta reimpostando.
  if (search.resetToken !== undefined) {
    return <ResetPasswordScreen token={search.resetToken} />
  }

  if (step === 'code') {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center px-4 py-10 sm:px-6">
        <div className="w-full max-w-sm">
          <div className="mb-7 flex flex-col items-center gap-4 text-center">
            <Logo showWordmark={false} size="lg" />
            <div className="space-y-1">
              <h1 className="text-xl font-semibold tracking-tight">Verifica in due passaggi</h1>
              <p className="text-muted-foreground text-sm">
                {useRecoveryCode
                  ? 'Inserisci uno dei codici di recupero salvati alla configurazione.'
                  : 'Apri l’app di autenticazione e inserisci il codice a sei cifre.'}
              </p>
            </div>
          </div>

          <form
            onSubmit={codeForm.handleSubmit(onSubmitCode)}
            noValidate
            className="border-border bg-card flex flex-col gap-4 rounded-lg border p-5 sm:p-6"
          >
            {formError ? (
              <Alert variant="danger" title={formError}>
                {rateLimitedFor ? `Riprova tra ${rateLimitedFor} secondi.` : null}
              </Alert>
            ) : null}

            <Field
              label={useRecoveryCode ? 'Codice di recupero' : 'Codice di verifica'}
              required
              error={codeForm.formState.errors.code?.message}
              hint={
                useRecoveryCode
                  ? 'Formato XXXXX-XXXXX. Ogni codice vale una volta sola.'
                  : 'Il codice cambia ogni 30 secondi.'
              }
            >
              {(props) => (
                <Input
                  {...props}
                  {...codeForm.register('code')}
                  // `one-time-code` fa proporre a iOS e Android il codice
                  // ricevuto o memorizzato: senza, va copiato a mano da un'altra
                  // app mentre scade.
                  autoComplete="one-time-code"
                  inputMode={useRecoveryCode ? 'text' : 'numeric'}
                  autoFocus
                  placeholder={useRecoveryCode ? 'ABCDE-12345' : '000000'}
                  className="text-center font-mono tracking-[0.3em]"
                />
              )}
            </Field>

            <Button
              type="submit"
              variant="primary"
              size="lg"
              loading={codeForm.formState.isSubmitting}
            >
              Verifica ed entra
            </Button>

            <div className="flex flex-col gap-2 text-center text-xs">
              <button
                type="button"
                onClick={() => {
                  setUseRecoveryCode((previous) => !previous)
                  codeForm.reset({ code: '' })
                }}
                className="text-primary-content underline-offset-4 hover:underline"
              >
                {useRecoveryCode
                  ? 'Ho di nuovo accesso all’app di autenticazione'
                  : 'Non ho l’app: usa un codice di recupero'}
              </button>
              <button
                type="button"
                onClick={backToCredentials}
                className="text-muted-foreground underline-offset-4 hover:underline"
              >
                Annulla e torna all’accesso
              </button>
            </div>
          </form>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center px-4 py-10 sm:px-6">
      <div className="w-full max-w-sm">
        <div className="mb-7 flex flex-col items-center gap-4 text-center">
          <Logo showWordmark={false} size="lg" />
          <div className="space-y-1">
            <h1 className="text-xl font-semibold tracking-tight">Pannello amministrativo</h1>
            <p className="text-muted-foreground text-sm">
              Accedi con un account che ha ruolo <code className="font-mono">admin</code>.
            </p>
          </div>
        </div>

        <form
          onSubmit={form.handleSubmit(onSubmit)}
          noValidate
          className="border-border bg-card flex flex-col gap-4 rounded-lg border p-5 sm:p-6"
        >
          {formError ? (
            <Alert variant="danger" title={formError}>
              {rateLimitedFor ? `Riprova tra ${rateLimitedFor} secondi.` : null}
            </Alert>
          ) : null}

          <Field label="Email" required error={form.formState.errors.email?.message}>
            {(props) => (
              <Input
                {...props}
                {...form.register('email')}
                type="email"
                autoComplete="username"
                inputMode="email"
                autoFocus
                placeholder="nome@gymmi.app"
              />
            )}
          </Field>

          <Field label="Password" required error={form.formState.errors.password?.message}>
            {(props) => (
              <Input
                {...props}
                {...form.register('password')}
                type="password"
                autoComplete="current-password"
                placeholder="••••••••"
              />
            )}
          </Field>

          <Button type="submit" variant="primary" size="lg" loading={form.formState.isSubmitting}>
            Accedi
          </Button>

          {/* «Password dimenticata» **dentro** il form del login e non in una pagina a
              parte: chi la cerca la cerca qui, nel momento in cui il tentativo è appena
              fallito. Prima non c'era, e un operatore che perdeva la password non aveva
              nessun modo di rientrare — doveva chiederla al titolare, che non la ha. */}
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground self-start text-xs underline underline-offset-4"
            onClick={() => setRecovering((value) => !value)}
          >
            Password dimenticata?
          </button>

          <p className="text-muted-foreground flex items-start gap-2 text-xs">
            <ShieldCheck className="mt-px size-3.5 shrink-0" aria-hidden />
            <span>
              Il login admin ha un rate limiting più severo. Ogni operazione di scrittura da questo
              pannello finisce nell'audit log.
            </span>
          </p>
        </form>

        {recovering ? <ForgotPasswordForm defaultEmail={form.getValues('email')} /> : null}

        {config.enableMocks ? (
          <Alert variant="warning" className="mt-4" title="Mock attivi">
            MSW intercetta le chiamate: entra con <code className="font-mono">admin@gymmi.app</code>{' '}
            e password <code className="font-mono">password123</code>.
          </Alert>
        ) : null}
      </div>
    </div>
  )
}

/**
 * Il primo dei due passi del recupero — `POST /auth/password/forgot`.
 *
 * ⚠️ **Il messaggio è lo stesso in ogni caso**, e non per pigrizia: il server risponde
 * `202` sia che l'indirizzo esista sia che no, perché una risposta diversa sarebbe un
 * oracolo per scoprire chi lavora qui. Dire «email inviata» quando l'indirizzo non
 * esiste non è una bugia dell'interfaccia: è l'unica cosa vera che si può dire, e la
 * schermata lo formula come condizione — «se l'indirizzo è registrato».
 */
function ForgotPasswordForm({ defaultEmail }: { defaultEmail: string }) {
  const [email, setEmail] = useState(defaultEmail)
  const [sent, setSent] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit() {
    setBusy(true)
    setError(null)
    try {
      await forgotPassword(email.trim())
      setSent(true)
    } catch (caught) {
      // Solo i guasti veri arrivano qui: rete giù, rate limiting. Il «non esiste» no.
      setError(errorMessage(caught))
    } finally {
      setBusy(false)
    }
  }

  if (sent) {
    return (
      <Alert variant="success" className="mt-4" title="Controlla la posta">
        Se l'indirizzo è registrato, è partita un'email con il link per reimpostare la password. Il
        link vale una volta sola e scade.
      </Alert>
    )
  }

  return (
    <div className="border-border bg-card mt-4 flex flex-col gap-3 rounded-lg border p-5">
      <p className="text-sm font-medium">Reimposta la password</p>
      <p className="text-muted-foreground text-xs">
        Arriva un'email con un link. Serve accesso alla casella: nessuno può reimpostarla per te,
        nemmeno il titolare della palestra.
      </p>
      <Field label="Email" required>
        {(props) => (
          <Input
            {...props}
            type="email"
            autoComplete="username"
            inputMode="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        )}
      </Field>
      {error === null ? null : <Alert variant="danger">{error}</Alert>}
      <Button size="sm" disabled={email.trim() === ''} loading={busy} onClick={() => void submit()}>
        Mandami il link
      </Button>
    </div>
  )
}

/**
 * Il secondo passo — `POST /auth/password/reset`, con il token del link.
 *
 * Non autentica: **invalida tutte le sessioni** dell'account e riporta al login. È il
 * verso giusto, ed è lo stesso del cambio password fatto per sospetto — chi reimposta
 * teme che qualcun altro sia dentro, e lasciare in piedi le altre sessioni vanificherebbe
 * il gesto.
 *
 * Token inesistente, già speso e scaduto sono lo stesso errore, come sugli inviti:
 * distinguerli direbbe a chi prova token a caso quali esistono.
 */
function ResetPasswordScreen({ token }: { token: string }) {
  const navigate = useNavigate()
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  const tooShort = password !== '' && password.length < 8
  const mismatch = confirm !== '' && confirm !== password

  async function submit() {
    setBusy(true)
    setError(null)
    try {
      await resetPassword({ token, newPassword: password })
      setDone(true)
    } catch (caught) {
      setError(errorMessage(caught))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center px-4 py-10 sm:px-6">
      <div className="w-full max-w-sm">
        <div className="mb-7 flex flex-col items-center gap-4 text-center">
          <Logo showWordmark={false} size="lg" />
          <h1 className="text-xl font-semibold tracking-tight">Nuova password</h1>
        </div>

        {done ? (
          <div className="border-border bg-card flex flex-col gap-3 rounded-lg border p-5">
            <Alert variant="success" title="Password cambiata">
              Le altre sessioni sono state chiuse. Accedi con la password nuova.
            </Alert>
            <Button onClick={() => void navigate({ to: '/login', search: {} })}>
              Vai all'accesso
            </Button>
          </div>
        ) : (
          <div className="border-border bg-card flex flex-col gap-4 rounded-lg border p-5">
            <Field
              label="Password nuova"
              required
              hint="Almeno 8 caratteri."
              error={tooShort ? 'Almeno 8 caratteri.' : undefined}
            >
              {(props) => (
                <Input
                  {...props}
                  type="password"
                  autoComplete="new-password"
                  autoFocus
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
              )}
            </Field>
            <Field
              label="Ripeti la password"
              required
              error={mismatch ? 'Le due password non coincidono.' : undefined}
            >
              {(props) => (
                <Input
                  {...props}
                  type="password"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(event) => setConfirm(event.target.value)}
                />
              )}
            </Field>
            {error === null ? null : <Alert variant="danger">{error}</Alert>}
            <Button
              variant="primary"
              size="lg"
              disabled={password.length < 8 || confirm !== password}
              loading={busy}
              onClick={() => void submit()}
            >
              Imposta la password
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
